import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode, unref as _unref, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-42562baa"),n=n(),_popScopeId(),n)
const _hoisted_1 = { id: "stats-component" }
const _hoisted_2 = {
  class: "",
  id: "counters"
}
const _hoisted_3 = {
  key: 0,
  class: "counter flex-col-reverse md:flex-row flex md:justify-between mb-10 md:mb-12"
}
const _hoisted_4 = { class: "w-full md:w-1/2 text-left" }
const _hoisted_5 = { class: "counter-label title-label" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("sub", null, "collective", -1))
const _hoisted_7 = { class: "counter-info pr-16" }
const _hoisted_8 = { class: "w-full md:w-1/2 text-right counter-value pt-12" }
const _hoisted_9 = { id: "charts" }
const _hoisted_10 = { class: "chart mb-10 md:mb-12 flex-col-reverse md:flex-row flex md:justify-between" }
const _hoisted_11 = { class: "w-full md:w-1/2 text-left" }
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "chart-label title-label" }, "Racial Composition", -1))
const _hoisted_13 = ["innerHTML"]
const _hoisted_14 = { class: "w-full md:w-1/2 chart-value mb-12" }
const _hoisted_15 = { class: "chart mb-10 md:mb-12 flex-col-reverse md:flex-row flex md:justify-between" }
const _hoisted_16 = { class: "w-full md:w-1/2 chart-value mb-12" }
const _hoisted_17 = { class: "w-full md:w-1/2 text-left" }
const _hoisted_18 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "chart-label title-label" }, "Gender Disparity", -1))
const _hoisted_19 = ["innerHTML"]
const _hoisted_20 = {
  class: "",
  id: "counters-small"
}
const _hoisted_21 = {
  key: 0,
  class: "counter block md:flex md:justify-between mb-10 md:mb-12"
}
const _hoisted_22 = { class: "w-full md:w-2/5 counter-value" }
const _hoisted_23 = { class: "w-full md:w-3/5 text-left" }
const _hoisted_24 = { class: "counter-label title-label" }
const _hoisted_25 = { class: "counter-info" }

import { ref, computed, watch } from 'vue';
import { DoughnutChart } from 'vue-chart-3';
import AnimatedNumber from './AnimatedNumber.vue';
import {PrisonerRecord} from "@/@types/types";
import { raceColor } from '@/composables/chartColors';

interface Props {
  records: PrisonerRecord[]
}

interface IDisplayElement {
  key: string
  label: string
  info: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NumbersComponent',
  props: {
    records: {}
  },
  setup(__props: any) {

const props = __props

const dataColors = [
  '#825af9',
  '#35d9c3',
  '#4375ff',
  '#6d3dbf',
  '#2cb1a1',
  '#3658d4'
]

const chartOptions = ref({
  responsive: true,
  elements: {
    arc: {
      borderWidth: 0
    }
  },
  plugins: {
    legend: {
      display: false
    },
    title: {
      display: false,
    },
  },
});

// Reactive computed stats based on filtered records
const sumCounts = computed(() => {
  const counts: Record<string, number> = {
    individualsNotReleased: 0,
    individualsInExile: 0,
    individualsImprisonedOrExiled: 0,
    individualsImprisoned: 0,
    accumulatedDaysImprisoned: 0,
    accumulatedDaysInExile: 0,
  }

  props.records.forEach((prisoner) => {
    if (prisoner['In Exile']) counts.individualsInExile++
    if (prisoner['In Custody']) counts.individualsImprisoned++
    if (prisoner['Imprisoned or Exiled'] === 'T') counts.individualsImprisonedOrExiled++
    if (prisoner.imprisonedFor) counts.accumulatedDaysImprisoned += prisoner.imprisonedFor
    if (prisoner.inExileFor) counts.accumulatedDaysInExile += prisoner.inExileFor
    if (!prisoner.Released) counts.individualsNotReleased++
  })

  return counts
})

const aggregateCounts = computed(() => {
  const agg: Record<string, Record<string, number>> = {
    Race: {},
    Gender: {},
    Era: {},
  }

  props.records.forEach((prisoner) => {
    const processKey = (key: string, value: any) => {
      if (!value) return
      if (Array.isArray(value)) {
        value.forEach((val: string) => {
          if (!agg[key][val]) agg[key][val] = 0
          agg[key][val]++
        })
      } else {
        if (!agg[key][value]) agg[key][value] = 0
        agg[key][value]++
      }
    }
    processKey('Race', prisoner.Race)
    processKey('Gender', prisoner.Gender)
    processKey('Era', prisoner.Era)
  })

  // Sort each by count descending
  Object.keys(agg).forEach(key => {
    agg[key] = Object.entries(agg[key])
      .sort((a, b) => b[1] - a[1])
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
  })

  return agg
})

const prepareChartData = (key: string) => {
  const data = aggregateCounts.value[key] || {}
  return {
    labels: Object.keys(data),
    datasets: [{
      data: Object.values(data),
      backgroundColor: Object.keys(data).map((label, index) =>
        key === 'Race' ? raceColor(label, dataColors[index % dataColors.length]) : dataColors[index % dataColors.length])
    }]
  }
}

const chartDataGender = computed(() => prepareChartData('Gender'))
const chartDataRace = computed(() => prepareChartData('Race'))

const renderAggregateCounts = (key: string): string => {
  const data = aggregateCounts.value[key] || {}
  const entries = Object.entries(data)
  return entries.map(([label, count], index) => {
    const fallback = dataColors[index % dataColors.length]
    const color = key === 'Race' ? raceColor(label, fallback) : fallback
    const peopleLabel = count === 1 ? 'person' : 'people'
    return `<span class="font-bold" style="color: ${color};">${count} ${label} ${peopleLabel}</span>`
  }).join(', ')
}

const raceInfo = computed(() => {
  return `The racial composition of political prisoners - ${renderAggregateCounts('Race')} - speaks volumes about the intersection of race and political dissent in the United States.<br/><br/>
  These statistics compel us to question how race influences both the likelihood of becoming a political prisoner and the experiences of these individuals within the justice system.`
})

const genderInfo = computed(() => {
  return `In examining the stark disparity between genders - ${renderAggregateCounts('Gender')} - the data reflects a broader societal narrative.<br/><br/>
  It's essential to delve deeper into these numbers to understand the underlying causes of such a vast gender gap in political imprisonment, considering factors like historical marginalization, societal expectations, and the different risks and challenges faced by each gender in their pursuit of justice and equality.`
})

const sumElementsSmallNumbers: Array<IDisplayElement> = [
  {
    key: 'individualsInExile',
    label: 'Individuals in Exile',
    info: 'The count of individuals in exile underscores the harsh realities faced by those who challenge systemic powers. Their exile is a stark reminder of the sacrifices made in the pursuit of social and political change.'
  },
  {
    key: 'individualsImprisoned',
    label: 'Individuals Imprisoned',
    info: 'This figure represents a broader narrative of political resistance, encompassing those who face incarceration and exile. Their stories are emblematic of a larger struggle for justice and the ongoing fight against systemic oppression.'
  }
]

const sumElements: Array<IDisplayElement> = [
  {
    key: 'accumulatedDaysImprisoned',
    label: 'Days Imprisoned',
    info: 'This number reflects the staggering amount of time individuals, often activists and dissidents, have been confined. It symbolizes the enduring struggle against systemic injustice and the long shadow cast by policies that disproportionately target marginalized communities.'
  },
  {
    key: 'accumulatedDaysInExile',
    label: 'Days in Exile',
    info: 'Exile, a forced separation from one\'s homeland, often results from standing against oppressive regimes. This figure represents not just days lost, but lives uprooted and voices silenced in the fight for justice and human rights.'
  }
]

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(sumElements, (element) => {
        return (_openBlock(), _createElementBlock(_Fragment, {
          key: element.key
        }, [
          (sumCounts.value[element.key] > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _hoisted_6,
                    _createTextVNode(_toDisplayString(element.label), 1)
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("p", null, _toDisplayString(element.info), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(AnimatedNumber, {
                    value: sumCounts.value[element.key] || 0
                  }, null, 8, ["value"])
                ])
              ]))
            : _createCommentVNode("", true)
        ], 64))
      }), 64))
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("div", _hoisted_11, [
          _hoisted_12,
          _createElementVNode("div", null, [
            _createElementVNode("p", { innerHTML: raceInfo.value }, null, 8, _hoisted_13)
          ])
        ]),
        _createElementVNode("div", _hoisted_14, [
          _createVNode(_unref(DoughnutChart), {
            options: chartOptions.value,
            chartData: chartDataRace.value
          }, null, 8, ["options", "chartData"])
        ])
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          _createVNode(_unref(DoughnutChart), {
            options: chartOptions.value,
            chartData: chartDataGender.value
          }, null, 8, ["options", "chartData"])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _hoisted_18,
          _createElementVNode("div", null, [
            _createElementVNode("p", { innerHTML: genderInfo.value }, null, 8, _hoisted_19)
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_20, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(sumElementsSmallNumbers, (element) => {
        return (_openBlock(), _createElementBlock(_Fragment, {
          key: element.key
        }, [
          (sumCounts.value[element.key] > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                _createElementVNode("div", _hoisted_22, [
                  _createVNode(AnimatedNumber, {
                    value: sumCounts.value[element.key] || 0
                  }, null, 8, ["value"])
                ]),
                _createElementVNode("div", _hoisted_23, [
                  _createElementVNode("div", _hoisted_24, _toDisplayString(element.label), 1),
                  _createElementVNode("div", _hoisted_25, [
                    _createElementVNode("p", null, _toDisplayString(element.info), 1)
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ], 64))
      }), 64))
    ])
  ]))
}
}

})